import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import useTheme from "../ColorConfig/usetheme";
import VaanOurProjectsHeader from "./OurProjectHeader";
import VaanOurProjectsCard from "./OurProjectCard";
import VaanOurProjectsStyles from "./OurProjectStyles";
import SEO from "../../AASeo/Seo"; // Add SEO import

function OurProjects() {
  const colors = useTheme();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleCards, setVisibleCards] = useState(new Set());
  const cardRefs = useRef([]);
  const observerRef = useRef(null);
  const containerRef = useRef(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scroll to top when category changes
  useEffect(() => {
    // Use setTimeout to ensure the scroll happens after the state update
    const timer = setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [selectedCategory]);

  // Scroll to top when navigating from other pages with category state
  useEffect(() => {
    if (location.state?.selectedCategory) {
      setSelectedCategory(location.state.selectedCategory);
      console.log("Category from navigation:", location.state.selectedCategory);

      // Scroll to top when coming from navigation with category
      const timer = setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [location.state]);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    console.log("Filtering projects. Selected category:", selectedCategory);
    console.log("Total projects:", projects.length);

    if (projects.length > 0) {
      let filtered = [];

      if (selectedCategory === "All") {
        filtered = [...projects];
        console.log("Showing all projects:", filtered.length);
      } else {
        filtered = projects.filter((project) => {
          const matches =
            project.category &&
            project.category.trim().toLowerCase() ===
              selectedCategory.trim().toLowerCase();

          if (matches) {
            console.log(
              "Project matched:",
              project.title,
              "Category:",
              project.category
            );
          }

          return matches;
        });
        console.log(
          "Filtered projects for category:",
          selectedCategory,
          "Count:",
          filtered.length
        );
      }

      const sortedFiltered = sortProjectsByPosition(filtered);
      setFilteredProjects(sortedFiltered);
    }
  }, [selectedCategory, projects]);

  // Sort projects by position (convert to number for proper sorting)
  const sortProjectsByPosition = (projectsArray) => {
    return projectsArray.sort((a, b) => {
      // Convert position to number for proper numerical sorting
      const posA = parseInt(a.position) || 0;
      const posB = parseInt(b.position) || 0;
      return posA - posB; // Ascending order (1, 2, 3, ...)
    });
  };

  useEffect(() => {
    if (filteredProjects.length === 0) return;

    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer with proper threshold
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setVisibleCards((prev) => new Set([...prev, index]));
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: "0px 0px -100px 0px",
      }
    );

    // Observe all cards
    cardRefs.current.forEach((ref) => {
      if (ref) {
        observerRef.current.observe(ref);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [filteredProjects]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        "https://api.dremerz.net/api/DZ_8923_Idlbay_OurProjects_OurProjectsList"
      );

      if (!response.ok) {
        throw new Error("Failed to fetch projects");
      }

      const data = await response.json();

      // Sort all projects by position when fetched
      const sortedProjects = sortProjectsByPosition(data);
      setProjects(sortedProjects);
      setError(null);
      setVisibleCards(new Set());
      cardRefs.current = [];
    } catch (err) {
      setError(err.message);
      console.error("Error fetching projects:", err);
    } finally {
      setLoading(false);
    }
  };

  // Extract unique categories from projects
  const getCategories = () => {
    const categories = ["All"]; // Always include 'All' option
    projects.forEach((project) => {
      if (project.category && !categories.includes(project.category)) {
        categories.push(project.category);
      }
    });
    return categories;
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setVisibleCards(new Set()); // Reset visibility for new filtered items

    // Scroll to top immediately when category changes
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleVisitSite = (link) => {
    window.open(link, "_blank", "noopener,noreferrer");
  };

  // Dynamic SEO meta based on selectedCategory
  const seoTitle =
    selectedCategory === "All"
      ? "Our Projects | IDLBAY"
      : `${selectedCategory} Projects | IDLBAY`;
  const seoDescription =
    selectedCategory === "All"
      ? "Explore our portfolio of completed projects at IDLBAY. Discover our expertise in IT solutions, software, and digital services."
      : `Explore our ${selectedCategory} projects portfolio at IDLBAY. Discover our expertise in ${selectedCategory}, IT solutions, software, and digital services.`;
  const seoKeywords =
    selectedCategory === "All"
      ? "IDLBAY, projects, portfolio, IT solutions, software, digital services"
      : `IDLBAY, ${selectedCategory}, projects, portfolio, IT solutions, software, digital services`;
  const seoUrl =
    selectedCategory === "All"
      ? "https://idlbay.com/ourprojects"
      : `https://idlbay.com/ourprojects?category=${encodeURIComponent(
          selectedCategory
        )}`;

  if (loading) {
    return (
      <div
        style={{
          background: colors?.bodyBlack || "#1A1A1A",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="text-center">
          <div
            className="spinner-border"
            style={{ color: colors?.primary || "#E57627" }}
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3" style={{ color: colors?.white || "#FFFFFF" }}>
            Loading projects...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          background: colors?.bodyBlack || "#1A1A1A",
          minHeight: "100vh",
          padding: "3rem 0",
        }}
      >
        <div className="container">
          <div
            className="alert text-center"
            role="alert"
            style={{
              backgroundColor: colors?.primaryLight || "#ffe7d7ff",
              borderColor: colors?.primary || "#E57627",
              color: colors?.bodyBlack || "#1A1A1A",
              border: `2px solid ${colors?.primary || "#E57627"}`,
            }}
          >
            <h4 className="alert-heading">Error loading projects</h4>
            <p>{error}</p>
            <button
              className="btn mt-2"
              onClick={fetchProjects}
              style={{
                backgroundColor: colors?.primary || "#E57627",
                color: colors?.white || "#FFFFFF",
                border: "none",
                padding: "10px 30px",
                borderRadius: "25px",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor =
                  colors?.primaryHover || "#D16A23";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = colors?.primary || "#E57627";
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        url={seoUrl}
      />
      <VaanOurProjectsStyles colors={colors} />

      <div className="vaanourproject-container" ref={containerRef}>
        <div className="container">
          {/* Section Header */}
          <VaanOurProjectsHeader
            selectedCategory={selectedCategory}
            categories={getCategories()}
            onCategoryChange={handleCategoryChange}
            totalProjects={filteredProjects.length}
          />

          {/* Projects Grid - Now sorted by position */}
          <div className="vaanourproject-grid row g-4">
            {filteredProjects.map((project, index) => {
              const animationClass =
                index % 2 === 0
                  ? "vaanourproject-slide-left"
                  : "vaanourproject-slide-right";
              const isVisible = visibleCards.has(index);

              return (
                <VaanOurProjectsCard
                  key={project.id}
                  project={project}
                  index={index}
                  animationClass={animationClass}
                  isVisible={isVisible}
                  cardRefs={cardRefs}
                  onVisitSite={handleVisitSite}
                />
              );
            })}
          </div>

          {/* Empty State */}
          {filteredProjects.length === 0 && !loading && (
            <div className="vaanourproject-empty text-center py-5">
              <i
                className="fas fa-folder-open mb-3"
                style={{
                  fontSize: "3rem",
                  color: colors?.gray || "#848484",
                }}
              ></i>
              <h4 style={{ color: colors?.white || "#FFFFFF" }}>
                No projects found
              </h4>
              <p className="fs-5" style={{ color: colors?.gray || "#848484" }}>
                {selectedCategory === "All"
                  ? "No projects available at the moment."
                  : `No projects found in the ${selectedCategory} category.`}
              </p>
              {selectedCategory !== "All" && (
                <button
                  className="btn mt-3"
                  onClick={() => handleCategoryChange("All")}
                  style={{
                    backgroundColor: colors?.primary || "#E57627",
                    color: colors?.white || "#FFFFFF",
                    border: "none",
                    padding: "10px 25px",
                    borderRadius: "25px",
                  }}
                >
                  View All Projects
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default OurProjects;
